import React, { useEffect, useState, useRef } from "react";
import { Grid, FormControl, FormControlLabel, Checkbox } from "@mui/material";
import { Controller } from "react-hook-form";
import { getByAgreementType, getCachedAgreementResponse, getSignedUndertaking } from "api";
import AgreementDialog from "./AgreementDialog";
import "./CommonDialog.css";
import moment from "moment";

export default function AgreementContainer(props) {
	const { type, serviceId, control, watch, errors, setValue, disabled, onTemplateStatusChange } = props;

	// Watch the undertakingSigned field and the form ID
	const isChecked = watch("undertakingSigned");
	const formId = watch("id");
	const projectId = watch("projectId");
	const userProfileId = watch("userProfileId");

	const cachedAgreement = getCachedAgreementResponse("type", {
		agreementType: type,
		serviceId,
		refProjectId: projectId || undefined,
	});
	const [agreementTitle, setAgreementTitle] = useState(cachedAgreement?.data?.title || "");
	const [agreementContent, setAgreementContent] = useState(cachedAgreement?.data?.content || "");
	const [openAgreement, setOpenAgreement] = useState(false);
	const [canAgree, setCanAgree] = useState(false);
	const [timeLeft, setTimeLeft] = useState(5);
	const [showAgree, setShowAgree] = useState(true);
	const [hasContent, setHasContent] = useState(() => Boolean(cachedAgreement?.data?.content));
	const [hasSignedUndertaking, setHasSignedUndertaking] = useState(false);
	const [signDate, setSignDate] = useState(null);
	const [approveDate, setApproveDate] = useState(null);
	const [version, setVersion] = useState(null);
	const [shouldRender, setShouldRender] = useState(Boolean(cachedAgreement?.data?.content)); // Control whether to show the component
	const hasFetchedRef = useRef(false); // Track if agreement has been fetched to prevent duplicate calls

	const agreementEntityId = type === "PROJECT" ? (projectId || formId) : formId;
	const isError = !!errors?.undertakingSigned;

	useEffect(() => {
		// Reset fetch flag when type changes (allows refetching for different agreement types)
		hasFetchedRef.current = false;
	}, [type]);

	useEffect(() => {
		// Prevent duplicate API calls
		if (hasFetchedRef.current) {
			return;
		}
		
		// For PROJECT type, determine which API to call based on submission status
		// If NOT signed/submitted (undertakingSigned is 0 or null), fetch the latest template
		// If signed (undertakingSigned is 1), fetch the signed version
		if (agreementEntityId) {
			const isSigned = isChecked === 1;
			
			if (type === "PROJECT" && !isSigned) {
				// Not signed yet (Invited status) - fetch latest template for user to review
				fetchAgreementWithStatus();
				hasFetchedRef.current = true;
			} else if (type === "PROJECT") {
				// Already signed - fetch signed undertaking
				fetchSignedUndertaking(agreementEntityId);
				hasFetchedRef.current = true;
			} else {
				// Non-PROJECT types with ID
				fetchSignedUndertaking(agreementEntityId);
				hasFetchedRef.current = true;
			}
		} else {
			// No ID - fetch current template
			fetchAgreementWithStatus();
			hasFetchedRef.current = true;
		}
	}, [type, formId, projectId, userProfileId, isChecked, agreementEntityId]);

	const fetchAgreementWithStatus = async () => {
		try {
			const res = await getByAgreementType({
				agreementType: type,
				serviceId,
				refProjectId: projectId || undefined, // Pass projectId so backend can fetch organization (for EXPERT invitations)
			});
			if (res.data) {
				const { title, content, hasSigned, signDate, approveDate, version: apiVersion } = res.data;
				setAgreementTitle(title);
				setAgreementContent(content);
				setVersion(apiVersion);
				// Check if content is available
				setHasContent(!!content);
				// If NO content/template exists, hide the checkbox
				if (!content) {
					setShouldRender(false);
					setValue("undertakingSigned", 0);
					// Notify parent that no template exists (upload mode)
					if (onTemplateStatusChange) {
						onTemplateStatusChange(false);
					}
					return;
				}
				setShouldRender(true);
				if (hasSigned) {
					setHasSignedUndertaking(true);
					setSignDate(signDate);
					setApproveDate(approveDate);
					// Auto-set the undertakingSigned field
					setValue("undertakingSigned", 1);
				} else {
					// Initialize to 0 when not signed
					setHasSignedUndertaking(false);
					setSignDate(signDate);
					setApproveDate(approveDate);
					setValue("undertakingSigned", 0);
				}
				// Notify parent that template exists (e-undertaking mode)
				if (onTemplateStatusChange) {
					onTemplateStatusChange(true);
				}
			}
			else {
				// No data returned - hide the checkbox
				setShouldRender(false);
				setHasContent(false);
				setHasSignedUndertaking(false);
				setSignDate(null);
				setApproveDate(null);
				setVersion(null);
				setValue("undertakingSigned", 0);
				// Notify parent that no template exists (upload mode)
				if (onTemplateStatusChange) {
					onTemplateStatusChange(false);
				}
			}
		} catch (error) {
			console.error("Error fetching agreement with status:", error);
		}
	};

	const fetchSignedUndertaking = async (id) => {
		try {
			const params = {
				agreementType: type,
			};
			
			// For PROJECT type, send projectId
			// For APPLICATION type, send applicationId
			if (type === "PROJECT") {
				params.projectId = id;
				if (userProfileId) {
					params.userProfileId = userProfileId;
				}
			} else {
				params.applicationId = id;
			}
			
			const res = await getSignedUndertaking(params);
			if (res.data && res.data.hasSigned) {
				const { title, content, signDate, approveDate } = res.data;
				setAgreementTitle(title);
				setAgreementContent(content);
				// Check if content is available
				setHasContent(!!content);
				// If NO content exists, hide the checkbox
				if (!content) {
					setShouldRender(false);
					setValue("undertakingSigned", 0);
					// Notify parent that no template exists (upload mode)
					if (onTemplateStatusChange) {
						onTemplateStatusChange(false);
					}
					return;
				}
				setShouldRender(true);
				setHasSignedUndertaking(true);
				setSignDate(signDate);
				setApproveDate(approveDate);
				// Auto-set the undertakingSigned field (read-only mode)
				setValue("undertakingSigned", 1);
				// Notify parent that template exists and is signed (e-undertaking mode)
				if (onTemplateStatusChange) {
					onTemplateStatusChange(true);
				}
			}
			else if (res.data && res.data.content) {
				// Template exists for organization but this application/project has not signed it yet.
				const { title, content, signDate, approveDate, version: apiVersion } = res.data;
				setAgreementTitle(title || "");
				setAgreementContent(content || "");
				setVersion(apiVersion || null);
				setShouldRender(true);
				setHasContent(true);
				setHasSignedUndertaking(false);
				setSignDate(signDate || null);
				setApproveDate(approveDate || null);
				setValue("undertakingSigned", 0);
				if (onTemplateStatusChange) {
					onTemplateStatusChange(true);
				}
			}
			else {
				// Neither a signed undertaking nor an organization template exists.
				// Do not show the e-undertaking checkbox; the parent can use upload mode.
				setShouldRender(false);
				setHasContent(false);
				setHasSignedUndertaking(false);
				setSignDate(null);
				setApproveDate(null);
				setValue("undertakingSigned", 0);
				// Notify parent that no template exists (upload mode) for existing applications
				if (onTemplateStatusChange) {
					onTemplateStatusChange(false);
				}
			}
		} catch (error) {
			console.error("Error fetching signed undertaking:", error);
		}
	};

	const onCloseAgreement = () => {
		setOpenAgreement(false);
	};

	const handleCheckboxChange = () => {
		if (!isChecked) {
			setShowAgree(true);
			setOpenAgreement(true);
		} else {
			setValue("undertakingSigned", Number(!isChecked));
		}
	};

	const checkBoxLabel = () => {
		const Undertakinglabel = () => {return <span
			style={{
				color: "#0366d6",
				cursor: "pointer",
			}}
			onClick={handleUndertakingClick}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					handleUndertakingClick();
				}
			}}
			role="button"
			tabIndex={0}
		>
			Confidentiality Undertaking
		</span>
		}
		// Show signDate when undertaking has been approved (approveDate exists)
		if (hasSignedUndertaking && approveDate && signDate) {
			const formattedDate = moment(signDate).format("DD/MM/YYYY HH:mm:ss");
			return <>{Undertakinglabel()} was signed at {formattedDate}.</>;
		}
		return <>I accept and agree to the {Undertakinglabel()}.</>;
	};
	
	const handleAgree = () => {
		if (canAgree) {
			setValue("undertakingSigned", 1, { shouldValidate: true });
			setOpenAgreement(false);
		}
	};

	useEffect(() => {
		if (openAgreement) {
			setCanAgree(false);
			const timer = setInterval(() => {
				setTimeLeft((prev) => {
					if (prev === 1) {
						clearInterval(timer);
						setCanAgree(true);
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(timer);
		} else {
			setTimeLeft(5);
		}
	}, [openAgreement]);

	const handleUndertakingClick = () => {
		setShowAgree(!isChecked);
		setOpenAgreement(true);
	};

	// Don't render if no undertaking template exists
	if (!shouldRender) {
		return null;
	}

	return (
		<>
			<FormControl>
				<Controller
					name="undertakingSigned"
					control={control}
					render={({ field }) => (
						<Grid container alignItems="center">
							<FormControlLabel
								control={
									<Checkbox
										{...field}
										checked={field.value === 1}
										onChange={handleCheckboxChange}
										disabled={disabled || (hasSignedUndertaking && approveDate)}
										sx={
											isError
												? {
														"& .MuiSvgIcon-root": {
															color: "red",
														},
													}
												: {}
										}
									/>
								}
								label={checkBoxLabel()}
								className="undertaking-signed"
							/>
						</Grid>
					)}
				/>
			</FormControl>
			<AgreementDialog
				open={openAgreement}
				onClose={onCloseAgreement}
				title={agreementTitle}
				content={agreementContent}
				onAgree={handleAgree}
				canAgree={canAgree}
				showAgree={showAgree && hasContent}
				timeLeft={timeLeft}
			/>
		</>
	);
}
